import { LazyFile } from "@shared";
import { dataToBlobUrl } from "./blob";
import { transcodeVideo } from "./videoTranscode";
const THUMBNAIL_DIMS = dimensions(30, 30);
const DEFAULT_JPEG_QUALITY = 0.75;
export const MAX_DIMENSIONS = dimensions(1500, 1500);
export const FREE_MAX_SIZES = {
    image: 1024 * 1024,
    video: 1024 * 1024 * 5,
    audio: 1024 * 1024,
    file: 1024 * 1024,
};
export const DIAMOND_MAX_SIZES = {
    image: 1024 * 1024 * 5,
    video: 1024 * 1024 * 50,
    audio: 1024 * 1024 * 20,
    file: 1024 * 1024 * 5,
};
// The most a transcode is expected to shrink a clip by (see messageContentFromFile)
const MAX_TRANSCODE_SHRINK = 25;
function dimensions(width, height) {
    return { width, height };
}
export function scaleToFit(toScale, maxDimensions) {
    const aspectRatio = toScale.width / toScale.height;
    const maxAspectRatio = maxDimensions.width / maxDimensions.height;
    if (toScale.width <= maxDimensions.width && toScale.height <= maxDimensions.height) {
        return dimensions(toScale.width, toScale.height);
    }
    if (aspectRatio > maxAspectRatio) {
        return dimensions(maxDimensions.width, Math.floor(maxDimensions.width / aspectRatio));
    }
    else {
        return dimensions(Math.floor(maxDimensions.height * aspectRatio), maxDimensions.height);
    }
}
export async function extractImageThumbnail(file) {
    const bitmap = await createImageBitmap(file);
    return changeDimensions(bitmap, file.type, dimensions(bitmap.width, bitmap.height));
}
export async function extractVideoThumbnail(file) {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true; // may help with autoplay/seek on some browsers
    video.src = objectUrl;
    try {
        await new Promise((resolve, reject) => {
            video.addEventListener("loadedmetadata", () => resolve(), { once: true });
            video.addEventListener("error", () => reject(new Error("failed to load video")), {
                once: true,
            });
        });
        // Seek to 1 seecond, should be okay with iOS
        video.currentTime = 1;
        // Wait for the seek to complete
        await new Promise((resolve, reject) => {
            const seekTimeout = setTimeout(() => reject(new Error("video seek timeout")), 5000);
            video.addEventListener("seeked", () => {
                clearTimeout(seekTimeout);
                resolve();
            }, { once: true });
            // TODO any fallbacks for video.readyState < 2?
        });
        const originalDim = dimensions(video.videoWidth, video.videoHeight);
        const resized = await Promise.all([
            changeDimensions(video, file.type, originalDim, THUMBNAIL_DIMS),
            changeDimensions(video, file.type, originalDim, scaleToFit(originalDim, dimensions(1000, 1000))),
        ]);
        return resized;
    }
    finally {
        // Always clean up the object URL
        URL.revokeObjectURL(objectUrl);
        video.src = "";
        // helps release memory
        video.load();
    }
}
export async function stripMetaDataAndResize(file, bitmap) {
    // Directly create bitmap from the File (works on web + Tauri, no tainting)
    // skipDataUrl: the caller (handleImageFile) only reads .data from the
    // result — the data URL would be a full second JPEG encode of the same
    // 1500x1500 canvas, used by no one.
    const result = await changeDimensions(bitmap, file.type, dimensions(bitmap.width, bitmap.height), MAX_DIMENSIONS, true);
    // Free memory as soon as we're done with the bitmap
    bitmap.close();
    return result;
}
export async function changeDimensions(source, mimeType, originalDimensions, newDimensions = THUMBNAIL_DIMS, skipDataUrl = false) {
    const { width, height } = scaleToFit(originalDimensions, newDimensions);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.drawImage(source, 0, 0, width, height);
    const resultMimeType = mimeType === "image/jpeg" ? "image/jpeg" : "image/png";
    // We deliberately avoid canvas.toBlob: on Samsung's release WebView its
    // async callback pipeline takes ~4s per call regardless of canvas size,
    // while synchronous canvas.toDataURL on the same canvas returns in
    // milliseconds. We encode once via toDataURL and recover the raw bytes
    // by base64-decoding the data: URL with atob — fetch(dataUrl) would be
    // simpler but Tauri's default CSP blocks data: URLs in connect-src.
    const url = canvas.toDataURL(resultMimeType, DEFAULT_JPEG_QUALITY);
    const arrayBuffer = dataUrlToArrayBuffer(url);
    return {
        dimensions: originalDimensions,
        // skipDataUrl avoids retaining the (potentially MB-sized) base64
        // string on the resize path, where the caller only reads .data.
        url: skipDataUrl ? "" : url,
        data: arrayBuffer,
    };
}
function dataUrlToArrayBuffer(dataUrl) {
    const commaIdx = dataUrl.indexOf(",");
    if (commaIdx < 0)
        throw new Error("Malformed data URL");
    const binaryString = atob(dataUrl.slice(commaIdx + 1));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}
export function fillMessage(msg) {
    if (msg.forwarded ||
        msg.content.kind === "meme_fighter_content" ||
        msg.content.kind === "daily_result")
        return false;
    if (msg.content.kind === "prize_content")
        return true;
    if (msg.content.kind === "image_content" ||
        msg.content.kind === "video_content" ||
        msg.content.kind === "giphy_content") {
        return ((msg.content.caption === undefined || msg.content.caption === "") &&
            msg.repliesTo === undefined);
    }
    else {
        return false;
    }
}
export function audioRecordingMimeType() {
    // prefer mp4 since it works on iOS and desktop, fallback to webm just in case
    if (MediaRecorder.isTypeSupported("audio/mp4")) {
        return "audio/mp4";
    }
    else if (MediaRecorder.isTypeSupported("audio/webm")) {
        return "audio/webm";
    }
    return undefined;
}
function reduceWaveform(channels) {
    const targetSamples = 120;
    const bits = 8;
    const maxLevel = (1 << bits) - 1;
    const frameCount = channels[0].length;
    const samplesPerBucket = frameCount / targetSamples;
    const samples = new Uint8Array(targetSamples);
    function summariseBlock(start, end) {
        let sumsq = 0;
        let count = 0;
        for (let c = 0; c < channels.length; c++) {
            const ch = channels[c];
            for (let i = start; i < end; i++) {
                const v = ch[i];
                sumsq += v * v;
                count++;
            }
        }
        return Math.sqrt(sumsq / Math.max(1, count));
    }
    const raw = new Float32Array(targetSamples);
    let maxRaw = 0;
    for (let s = 0; s < targetSamples; s++) {
        const start = Math.floor(s * samplesPerBucket);
        const end = Math.min(Math.floor((s + 1) * samplesPerBucket), frameCount);
        const val = summariseBlock(start, end);
        raw[s] = val;
        if (val > maxRaw)
            maxRaw = val;
    }
    const normaliser = maxRaw > 0 ? 1 / maxRaw : 1;
    for (let s = 0; s < targetSamples; s++) {
        const q = Math.round(raw[s] * normaliser * maxLevel);
        samples[s] = Math.min(maxLevel, Math.max(0, q));
    }
    return samples;
}
/** A voice message's stored waveform (a byte per bar) as levels from 0 to 1. */
export function waveformLevels(samples) {
    return Array.from(samples, (s) => s / 255);
}
export async function quantiseWaveform(bytes) {
    const ctx = new AudioContext();
    const audioBuffer = await ctx.decodeAudioData(bytes);
    const channels = [];
    for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
        channels.push(audioBuffer.getChannelData(c));
    }
    const samples = reduceWaveform(channels);
    const durationMs = BigInt(Math.floor(audioBuffer.duration * 1000));
    return { samples, durationMs };
}
function mimeToMediaType(mimeType) {
    if (/^video/.test(mimeType))
        return "video";
    if (/^audio/.test(mimeType))
        return "audio";
    if (mimeType === "image/svg+xml")
        return "svg";
    if (/^image/.test(mimeType)) {
        if (/gif/.test(mimeType))
            return "gif";
        else
            return "image";
    }
    return "file";
}
// Process selected image!
//
// Creates a thumbnail, and resizes the image if necessary. Returns the content
// data shape.
// TODO blob data should be loaded lazyily for any content
async function handleImageFile(file, maxSizes, mediaType) {
    const isSvg = mediaType === "svg";
    let thumbnailData; // data URL for the thumbnail
    let thumbWidth;
    let thumbHeight;
    let originalData;
    if (isSvg) {
        // Special handling for SVG: render it to a canvas for raster thumbnail
        const result = await createSvgThumbnail(file, /* desired max size */ 300); // adjust size as needed
        thumbnailData = result.dataUrl;
        thumbWidth = result.width;
        thumbHeight = result.height;
        originalData = await file.arrayBuffer();
    }
    else {
        // Raster images (jpg, png, webp, etc.)
        const bitmap = await createImageBitmap(file);
        const thumbnail = await changeDimensions(bitmap, file.type, dimensions(bitmap.width, bitmap.height));
        thumbnailData = thumbnail.url;
        thumbWidth = thumbnail.dimensions.width;
        thumbHeight = thumbnail.dimensions.height;
        // Only re-encode when the image truly needs it. Re-encoding an
        // already-small image just to strip EXIF would force every share
        // through canvas, which is a few hundred ms even on the fast path.
        // Gate on file size (the actual upload ceiling) with a high
        // pixel-dimension safety net for pathological "tiny file, enormous
        // bitmap" cases that would blow up receiver memory on decode.
        const needsResize = file.size > maxSizes.image || bitmap.width > 5000 || bitmap.height > 5000;
        if (needsResize) {
            // stripMetaDataAndResize() takes ownership and closes the bitmap.
            originalData = (await stripMetaDataAndResize(file, bitmap)).data;
        }
        else {
            originalData = await file.arrayBuffer();
            bitmap.close();
        }
    }
    const blobUrl = dataToBlobUrl(originalData, file.type);
    return {
        kind: "image_content",
        mimeType: isSvg ? "image/svg+xml" : file.type,
        width: thumbWidth,
        height: thumbHeight,
        blobData: new Uint8Array(originalData),
        thumbnailData: thumbnailData,
        blobUrl: blobUrl,
    };
}
// Handle video files
//
// Transcodes to 720p H.264 where the source is bigger than that or not H.264
// (and the browser has WebCodecs), extracts a thumbnail and a full image,
// then reads the video data and returns the attachment content. Thumbnails
// come from the transcoded file so a source this browser can't play (HEVC on
// Chrome without hardware decode) still gets one.
//
// The size cap applies to the bytes that will actually be uploaded, so it is
// checked after the transcode: a 24MB phone clip that comes out at 4MB is
// fine, and only what the cap is for - the stored size - is measured. The
// worker refuses up front any clip whose duration alone puts the output over
// the cap; the encoder's bitrate is a target rather than a bound though, and
// the audio track is copied through untouched, so a transcode can still land
// just over it. An original that fits is then uploaded instead of failing.
// TODO blob data should be loaded lazyily for any content
async function handleVideoFile(original, maxBytes, transcode) {
    const transcoded = transcode ? await transcodeVideo(original, maxBytes, transcode) : undefined;
    const useTranscode = transcoded !== undefined && (transcoded.file.size <= maxBytes || original.size > maxBytes);
    const file = useTranscode ? transcoded.file : original;
    if (file.size > maxBytes)
        throw "maxVideoSize";
    const [thumb, image] = await extractVideoThumbnail(file);
    const data = await file.arrayBuffer();
    const blobUrl = dataToBlobUrl(data, file.type);
    return {
        kind: "video_content",
        mimeType: file.type,
        width: image.dimensions.width,
        height: image.dimensions.height,
        imageData: {
            blobData: new Uint8Array(image.data),
            blobUrl: image.url,
        },
        videoData: {
            blobData: new Uint8Array(data),
            blobUrl: blobUrl,
        },
        thumbnailData: thumb.url,
        sourceHash: useTranscode ? transcoded.sourceHash : undefined,
    };
}
// Handle audio files
//
// Waveform is quantised and attachment content returned.
// TODO - blob data should be loaded lazyily for any content
// TODO - should we be concerned about reducing audio size?
async function handleAudioFiles(file) {
    const data = await file.arrayBuffer();
    const blobUrl = dataToBlobUrl(data, file.type);
    const quantised = await quantiseWaveform(data.slice(0));
    return {
        kind: "audio_content",
        mimeType: file.type,
        blobData: new Uint8Array(data),
        blobUrl: blobUrl,
        ...quantised,
    };
}
// Handle regular files
//
// Anything that's not an image, video, audio...
// TODO extract an image from the file where possible (i.e. first page of pdf document)
async function handleRegularFiles(file) {
    const data = await file.arrayBuffer();
    const blobUrl = dataToBlobUrl(data, file.type);
    return {
        kind: "file_content",
        name: file.name,
        mimeType: file.type,
        blobData: new Uint8Array(data),
        blobUrl: blobUrl,
        fileSize: data.byteLength,
    };
}
export async function messageContentFromFile(file, isDiamond, transcode) {
    const dataSizeInBytes = file.size;
    const mediaType = mimeToMediaType(file.type);
    const maxSizes = isDiamond ? DIAMOND_MAX_SIZES : FREE_MAX_SIZES;
    switch (mediaType) {
        case "image":
        case "gif":
        case "svg": {
            // Force the bytes into the JS heap up-front. A real File (system
            // picker / drag-drop) is backed lazily by the underlying file on
            // disk; re-wrapping it around an ArrayBuffer pays the read once,
            // eagerly, so the later createImageBitmap/canvas draw doesn't
            // stall for seconds realising pixels. The LazyFile path already
            // materialises inside load(), so it needs no extra read.
            const f = file instanceof LazyFile
                ? await file.load()
                : new File([await file.arrayBuffer()], file.name, { type: file.type });
            return await handleImageFile(f, maxSizes, mediaType);
        }
        case "video": {
            // Refuse before the bytes are pulled into the heap: without WebCodecs nothing
            // can shrink the clip, and with it no transcode gets a clip this far over the
            // cap under it (the worker targets 2Mbps; phone footage tops out ~25x that)
            const canTranscode = transcode !== undefined && typeof VideoEncoder !== "undefined";
            if (dataSizeInBytes > (canTranscode ? MAX_TRANSCODE_SHRINK : 1) * maxSizes.video) {
                throw "maxVideoSize";
            }
            return await handleVideoFile(file instanceof LazyFile ? await file.load() : file, maxSizes.video, transcode);
        }
        case "audio":
            if (dataSizeInBytes > maxSizes.audio)
                throw "maxAudioSize";
            return await handleAudioFiles(file instanceof LazyFile ? await file.load() : file);
        // File is default!
        default:
            if (dataSizeInBytes > maxSizes.file)
                throw "maxFileSize";
            return await handleRegularFiles(file instanceof LazyFile ? await file.load() : file);
    }
}
// https://oc.app/community/yf5kc-uaaaa-aaaar-a7qfq-cai/channel/2656124989/1863
// or https://oc.app/community/yf5kc-uaaaa-aaaar-a7qfq-cai/channel/2656124989/1863/2
export const communityMessageRegex = () => /\/community\/([a-z0-9_-]+)\/channel\/(\d+)\/(\d+)(?:\/(\d+))?/i;
// https://oc.app/group/s5ihe-dqaaa-aaaac-a3elq-cai/172
// or https://oc.app/group/s5ihe-dqaaa-aaaac-a3elq-cai/172/3
export const groupMessageRegex = () => /\/group\/([a-z0-9_-]+)\/(\d+)(?:\/(\d+))?/i;
async function createSvgThumbnail(svgFile, maxDimension = 400) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const svgText = e.target?.result;
            if (!svgText) {
                reject(new Error("Failed to read SVG file"));
                return;
            }
            // Parse SVG to determine intrinsic dimensions
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
            const svgElement = svgDoc.documentElement;
            // Get width/height attributes or fall back to viewBox
            let origWidth = parseFloat(svgElement.getAttribute("width") || "0");
            let origHeight = parseFloat(svgElement.getAttribute("height") || "0");
            if (!origWidth || !origHeight) {
                const viewBox = svgElement.getAttribute("viewBox");
                if (viewBox) {
                    const parts = viewBox.trim().split(/\s+/);
                    if (parts.length === 4) {
                        origWidth = parseFloat(parts[2]);
                        origHeight = parseFloat(parts[3]);
                    }
                }
            }
            // Final fallback if still no size (very common with SVGs)
            if (!origWidth || !origHeight || isNaN(origWidth) || isNaN(origHeight)) {
                origWidth = 200;
                origHeight = 200;
            }
            // Calculate thumbnail size while preserving aspect ratio
            const scale = Math.min(maxDimension / origWidth, maxDimension / origHeight, 1);
            const thumbWidth = Math.round(origWidth * scale);
            const thumbHeight = Math.round(origHeight * scale);
            // Create offscreen canvas
            const canvas = document.createElement("canvas");
            canvas.width = thumbWidth;
            canvas.height = thumbHeight;
            const ctx = canvas.getContext("2d", { alpha: true });
            if (!ctx) {
                reject(new Error("Could not get 2D canvas context"));
                return;
            }
            const img = new Image();
            img.onload = () => {
                // Draw the SVG onto the canvas at the desired thumbnail size
                ctx.drawImage(img, 0, 0, thumbWidth, thumbHeight);
                // Convert to PNG data URL (high quality)
                const dataUrl = canvas.toDataURL("image/png", 0.95);
                // Clean up the blob URL
                URL.revokeObjectURL(img.src);
                resolve({
                    dataUrl,
                    width: thumbWidth,
                    height: thumbHeight,
                });
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src); // still clean up on error
                reject(new Error("Failed to load SVG into Image element"));
            };
            // Use blob URL — this is reliable for SVGs
            const blobUrl = URL.createObjectURL(svgFile);
            img.src = blobUrl;
        };
        reader.onerror = () => reject(new Error("Failed to read SVG as text"));
        reader.readAsText(svgFile);
    });
}
